import { WP_REST_API_Error, WP_REST_API_Page, WP_REST_API_Post, WP_REST_API_Tags, WP_REST_API_Users } from 'wp-types'

import { PageType, PostType } from '@/types/wordpress'

import { cachedFetch } from './cache.service'

type WPRestAPIPostExtended = WP_REST_API_Post & {
  yoast_head_json: {
    og_image: { url: string }[]
  }
}

type WPRestAPIPostsExtended = WPRestAPIPostExtended[]

const baseUrl = 'https://edhrec.com/articles'

const fetchUrl = async <T>(url: string, options: any): Promise<T | undefined> => {
  console.log(url)
  const headers: Record<string, string> = {}

  // Build request headers
  if (options.cookies) {
    const allowedCookies = ['wordpress_logged_in_', 'wp-rest-auth', 'wordpress_sec_']
    const cookieHeader = Object.entries(options.cookies)
      .filter(([key]) => allowedCookies.some((allowed) => key.startsWith(allowed)))
      .map(([key, value]) => `${key}=${value}`)
      .join('; ')

    if (cookieHeader) {
      headers['Cookie'] = cookieHeader
    }
  }

  let fetcher
  if (options.token) {
    headers['Authorization'] = `Bearer ${options.token}`
    fetcher = fetch
    console.log('Fetching with token')
  } else {
    fetcher = cachedFetch
    console.log('Cached fetching')
  }

  // Send request and check status
  const res = await fetcher(url, { headers })
  if (options.logging) {
    console.log(res)
  }
  if (res.status === 401) {
    throw new Error('Unauthorized')
  } else if (res.status === 404) {
    return undefined
  }

  // Log response headers
  console.log(res.headers)

  // Check for valid response
  const data = await res.json()
  if ((data as WP_REST_API_Error).data?.status === 403) {
    throw new Error('Signature verification failed')
  }

  return Array.isArray(data) ? (data[0] as T) : (data as T)
}

export const formatTag = (tag: string): string => tag.replace(/-/g, ' ').replace('#', '').toLowerCase()

const getAuthorId = async (author: string): Promise<number | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/users?slug=${author}`
  const res = await cachedFetch(url)
  const authors = (await res.json()) as WP_REST_API_Users
  return authors[0]?.id
}

export const getPage = async ({
  id,
  slug,
  token,
}: {
  id?: string
  slug?: string
  token?: string
}): Promise<PageType | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/pages${slug ? `?slug=${slug}&_embed` : `/${id}/revisions?skip_cache=1`}`
  const page = await fetchUrl<WP_REST_API_Page>(url, { token })
  if (!page) return undefined

  // The revisions endpoint doesn't include embeds or ACF data, so we need to fetch those separately
  if (id) {
    const pageWithEmbeds = await fetchUrl<WPRestAPIPostExtended>(
      `${baseUrl}/wp-json/wp/v2/pages/${id}?skip_cache=1&_embed`,
      { token },
    )
    if (!pageWithEmbeds) return undefined
    page._embedded = page._embedded
    page.acf = page.acf // Requires ACF to REST API plugin
  }

  return parsePage(page)
}

export const getPost = async ({
  cookies,
  id,
  slug,
  token,
}: {
  cookies?: any
  id?: string
  slug?: string
  token?: string
}): Promise<PostType | undefined> => {
  const url = id
    ? `${baseUrl}/wp-json/wp/v2/posts/${id}/revisions?skip_cache=1`
    : `${baseUrl}/wp-json/wp/v2/posts?slug=${slug}&_embed`
  const post = await fetchUrl<WPRestAPIPostExtended>(url, { cookies, token })
  if (!post) return undefined

  // The revisions endpoint doesn't include embeds, so we need to fetch those separately
  if (id) {
    const postWithEmbeds = await fetchUrl<WPRestAPIPostExtended>(`${baseUrl}/wp-json/wp/v2/posts/${id}?_embed`, {
      cookies,
      token,
    })
    if (!postWithEmbeds) return undefined
    post._embedded = postWithEmbeds._embedded
  }

  return parsePost(post)
}

export const getPosts = async ({
  author,
  categories,
  page = 1,
  search,
  tag,
}: {
  author?: string
  categories?: number[]
  page: number
  search?: string
  tag?: string
}) => {
  if (isNaN(page) || !Number.isInteger(page) || page < 1) {
    throw new Error('Page must be a positive integer')
  }

  const params = new URLSearchParams({
    _embed: '',
    page: `${page}`,
  })

  const errorResponse = {
    pageCount: 0,
    posts: [],
  }

  if (author) {
    const authorId = await getAuthorId(author)
    if (!authorId) return errorResponse
    params.append('author', `${authorId}`)
  }

  if (categories) {
    params.append('categories', categories.join(','))
  }

  if (search) {
    params.append('search', search)
  }

  if (tag) {
    const tagId = await getTagId(tag)
    if (!tagId) return errorResponse
    params.append('tags', `${tagId}`)
  }

  const url = `${baseUrl}/wp-json/wp/v2/posts?${params.toString()}`
  console.log(url)

  const res = await cachedFetch(url)
  const pageCount = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10)

  const rawPosts = (await res.json()) as WPRestAPIPostsExtended | WP_REST_API_Error

  if ((rawPosts as WP_REST_API_Error).data?.status === 400) {
    throw new Error((rawPosts as WP_REST_API_Error).message)
  }

  const posts = (rawPosts as WPRestAPIPostsExtended).map(parsePost)

  return { pageCount, posts }
}

const getTagId = async (tag: string): Promise<number | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/tags?slug=${tag}`
  const res = await cachedFetch(url)
  const tags = (await res.json()) as WP_REST_API_Tags
  return tags[0]?.id
}

export const login = async (username: string, password: string) => {
  const res = await fetch('https://edhrec.com/articles/wp-json/jwt-auth/v1/token', {
    body: JSON.stringify({ password, username }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  if (!res.ok) {
    throw new Error('Login failed')
  }

  const data = await res.json()

  return data
}

const parseExcerpt = (content: string): string => {
  const match = content.match(/^<p>(.*?)<\/p>\n$/)
  return match ? match[1] : content
}

export const parsePage = (page: WP_REST_API_Page): PageType => ({
  content: page.content.rendered,
  title: page.title.rendered,
})

export const parsePost = (post: WPRestAPIPostExtended): PostType => {
  const { _embedded } = post as any
  const rawCategories = _embedded['wp:term'].filter((term: any) => term[0]?.taxonomy === 'category')[0]
  const rawTags = _embedded['wp:term'].filter((term: any) => term[0]?.taxonomy === 'post_tag')[0]
  const { guide } = post.acf as any
  const featuredImageUrl = post.yoast_head_json?.og_image?.[0]?.url || _embedded['wp:featuredmedia']?.[0]?.source_url

  const parsedPost = {
    author: {
      avatarUrl: _embedded.author[0].avatar_urls['96'],
      description: _embedded.author[0].description,
      name: _embedded.author[0].name,
      slug: _embedded.author[0].slug,
    },
    categories: rawCategories ? rawCategories.map(({ slug }: { slug: string }) => slug) : [],
    content: post.content.rendered,
    databaseId: `${post.id}`,
    date: post.date,
    excerpt: parseExcerpt(post.excerpt.rendered),
    slug: post.slug,
    tags: rawTags ? rawTags.map(({ name, slug }: { name: string; slug: string }) => ({ name, slug })) : [],
    title: post.title.rendered,
  } as PostType

  if (featuredImageUrl) parsedPost.featuredImageUrl = featuredImageUrl
  if (guide) parsedPost.guide = guide

  return parsedPost
}
